import React, { useMemo, useState } from 'react'

import {
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    Theme,
    Tooltip,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import SearchIcon from '@material-ui/icons/Search'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import AddIcon from '@material-ui/icons/Add'
import StarIcon from '@material-ui/icons/Star'

import { Container } from '../../../../components'
import {
    CreateServiceRequest,
    ListVendor,
    Service,
    ServiceCategoryCode,
    SERVICE_CATEGORY_LABELS,
    SERVICE_CATEGORY_ORDER,
    getServiceImageDetails,
} from '../../../../models'
import { CreateServiceActionThunk } from '../../../../store/service'
import { VENDOR_MODAL_H, VENDOR_MODAL_W } from './contants'
import { typographyStyle } from '../../../../theme/typographyStyle'
import { toast } from 'react-toastify'
import {
    DEFAULT_ADMIN_ICON,
    DEFAULT_CLEAN_ICON,
    DEFAULT_FLOORING_ICON,
    DEFAULT_MAINTENANCE_ICON,
    DEFAULT_PAINT_ICON,
} from '../../../../assets/service-icons'

type CategoryKey = ServiceCategoryCode | 'OTHER'

const CATEGORY_KEYS: CategoryKey[] = [...SERVICE_CATEGORY_ORDER, 'OTHER']

const CATEGORY_ACCENTS: Record<CategoryKey, string> = {
    MAINTENANCE: '#4A6670',
    PAINT: '#016FB9',
    CLEAN: '#C62828',
    FLOORING: '#5C3D5E',
    ADMIN: '#546E7A',
    OTHER: '#78909C',
}

const CATEGORY_ICONS: Record<CategoryKey, string | null> = {
    PAINT: DEFAULT_PAINT_ICON,
    CLEAN: DEFAULT_CLEAN_ICON,
    FLOORING: DEFAULT_FLOORING_ICON,
    MAINTENANCE: DEFAULT_MAINTENANCE_ICON,
    ADMIN: DEFAULT_ADMIN_ICON,
    OTHER: null,
}

const getCategoryKey = (service: Service): CategoryKey => {
    if (
        service.service_category &&
        SERVICE_CATEGORY_ORDER.includes(service.service_category)
    ) {
        return service.service_category
    }
    return 'OTHER'
}

const getCategoryLabel = (key: CategoryKey) =>
    key === 'OTHER' ? 'Other' : SERVICE_CATEGORY_LABELS[key]

const COLOR_PRESETS = [
    '#46b083',
    '#016FB9',
    '#C62828',
    '#5C3D5E',
    '#F5A623',
    '#6A1B9A',
]

interface Props {
    serviceList: Service[]
    vendor: ListVendor
    theme: Theme
    onAddService: (vendor: ListVendor, service: Service) => void
    onRemoveService: (vendor: ListVendor, service: Service) => void
    onCreateService: (req: CreateServiceRequest) => CreateServiceActionThunk
    onPressNext: () => void
}

export const StepSelectServices = (props: Props) => {
    const { theme, vendor, serviceList, onPressNext, onCreateService } = props
    const [searchText, setSearchText] = useState('')
    const [activeCategory, setActiveCategory] = useState<CategoryKey | 'ALL'>(
        'ALL',
    )

    const selectedIds = useMemo(() => {
        return new Set(vendor.services.map((s) => s.id))
    }, [vendor.services])

    const groupedServices = useMemo(() => {
        const query = searchText.trim().toLowerCase()
        const groups = CATEGORY_KEYS.reduce((acc, key) => {
            acc[key] = []
            return acc
        }, {} as Record<CategoryKey, Service[]>)

        serviceList
            .filter((service) => {
                if (!query) return true
                return service.name.toLowerCase().includes(query)
            })
            .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
            .forEach((service) => {
                groups[getCategoryKey(service)].push(service)
            })

        return groups
    }, [serviceList, searchText])

    const isSearching = searchText.trim().length > 0

    const visibleCategories = useMemo(() => {
        if (isSearching) {
            return CATEGORY_KEYS.filter(
                (key) => groupedServices[key].length > 0,
            )
        }

        const categories: CategoryKey[] = [...SERVICE_CATEGORY_ORDER]
        if (groupedServices.OTHER.length > 0) {
            categories.push('OTHER')
        }
        if (activeCategory === 'ALL') {
            return categories
        }
        return categories.filter((key) => key === activeCategory)
    }, [groupedServices, isSearching, activeCategory])

    const selectedCountByCategory = useMemo(() => {
        return CATEGORY_KEYS.reduce((acc, key) => {
            acc[key] = groupedServices[key].filter((s) =>
                selectedIds.has(s.id),
            ).length
            return acc
        }, {} as Record<CategoryKey, number>)
    }, [groupedServices, selectedIds])

    const categoriesToRender =
        activeCategory === 'ALL'
            ? visibleCategories
            : visibleCategories.filter((key) => key === activeCategory)

    const HEADER_H = 112
    const FOOTER_H = 60
    const bodyMaxHeight = VENDOR_MODAL_H - HEADER_H - FOOTER_H

    const toggleService = (service: Service) => {
        if (selectedIds.has(service.id)) {
            props.onRemoveService(vendor, service)
        } else {
            props.onAddService(vendor, service)
        }
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                overflow: 'hidden',
                maxHeight: VENDOR_MODAL_H,
                minHeight: VENDOR_MODAL_H,
                minWidth: VENDOR_MODAL_W,
                maxWidth: VENDOR_MODAL_W,
            }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    minHeight: HEADER_H,
                    maxHeight: HEADER_H,
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        marginBottom: theme.spacing(1.5),
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                            color: theme.palette.grey[800],
                        }}
                    >
                        Select Services for {vendor.name}
                    </span>
                    <div style={{ flex: 1 }} />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.grey[600],
                            fontWeight: 600,
                        }}
                    >
                        {vendor.services.length} selected
                    </span>
                </Container>

                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search services..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    style={{ color: theme.palette.grey[500] }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Container>

            <Container
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    maxHeight: bodyMaxHeight,
                    minHeight: bodyMaxHeight,
                }}
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        minWidth: 168,
                        maxWidth: 168,
                        borderRight: `1px solid ${theme.palette.grey[300]}`,
                        backgroundColor: theme.palette.grey[50],
                        overflowY: 'auto',
                        padding: theme.spacing(1),
                    }}
                >
                    <CategoryNavButton
                        label="All"
                        count={serviceList.length}
                        selectedCount={vendor.services.length}
                        active={activeCategory === 'ALL'}
                        accent={theme.palette.primary.main}
                        theme={theme}
                        onClick={() => setActiveCategory('ALL')}
                    />
                    {SERVICE_CATEGORY_ORDER.map((key) => (
                        <CategoryNavButton
                            key={key}
                            label={getCategoryLabel(key)}
                            count={groupedServices[key].length}
                            selectedCount={selectedCountByCategory[key]}
                            active={activeCategory === key}
                            accent={CATEGORY_ACCENTS[key]}
                            theme={theme}
                            onClick={() => setActiveCategory(key)}
                        />
                    ))}
                    {groupedServices.OTHER.length > 0 && (
                        <CategoryNavButton
                            label="Other"
                            count={groupedServices.OTHER.length}
                            selectedCount={selectedCountByCategory.OTHER}
                            active={activeCategory === 'OTHER'}
                            accent={CATEGORY_ACCENTS.OTHER}
                            theme={theme}
                            onClick={() => setActiveCategory('OTHER')}
                        />
                    )}
                </Container>

                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        overflowY: 'auto',
                        padding: theme.spacing(2),
                    }}
                >
                    {categoriesToRender.length === 0 ? (
                        <Container
                            style={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.grey[500],
                                }}
                            >
                                No services match your search
                            </span>
                        </Container>
                    ) : (
                        categoriesToRender.map((key) => (
                            <CategorySection
                                key={key}
                                categoryKey={key}
                                services={groupedServices[key]}
                                selectedIds={selectedIds}
                                theme={theme}
                                onToggle={toggleService}
                                onCreateService={
                                    key === 'OTHER' || isSearching
                                        ? undefined
                                        : onCreateService
                                }
                                onCreated={(service) =>
                                    props.onAddService(vendor, service)
                                }
                            />
                        ))
                    )}
                </Container>
            </Container>

            <Container
                style={{
                    minHeight: FOOTER_H,
                    maxHeight: FOOTER_H,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: theme.spacing(1, 2),
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    disabled={vendor.services.length === 0}
                    style={{ height: 40, textTransform: 'none' }}
                    onClick={onPressNext}
                >
                    Next
                </Button>
            </Container>
        </Container>
    )
}

interface CategoryNavButtonProps {
    label: string
    count: number
    selectedCount: number
    active: boolean
    accent: string
    theme: Theme
    onClick: () => void
}

const CategoryNavButton = (props: CategoryNavButtonProps) => {
    const {
        label,
        count,
        selectedCount,
        active,
        accent,
        theme,
        onClick,
    } = props

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                border: 'none',
                borderLeft: active
                    ? `3px solid ${accent}`
                    : '3px solid transparent',
                borderRadius: theme.shape.borderRadius,
                backgroundColor: active ? `${accent}14` : 'transparent',
                padding: theme.spacing(1.25, 1.5),
                marginBottom: theme.spacing(0.5),
                cursor: 'pointer',
                textAlign: 'left',
            }}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.subtitle2),
                    fontWeight: 700,
                    color: active ? accent : theme.palette.grey[800],
                    lineHeight: 1.2,
                }}
            >
                {label}
            </span>
            <span
                style={{
                    ...typographyStyle(theme.typography.caption),
                    color: theme.palette.grey[600],
                    marginTop: 2,
                }}
            >
                {selectedCount > 0
                    ? `${selectedCount} of ${count}`
                    : `${count} services`}
            </span>
        </button>
    )
}

interface CategorySectionProps {
    categoryKey: CategoryKey
    services: Service[]
    selectedIds: Set<number>
    theme: Theme
    onToggle: (service: Service) => void
    onCreateService?: (req: CreateServiceRequest) => CreateServiceActionThunk
    onCreated?: (service: Service) => void
}

const CategorySection = (props: CategorySectionProps) => {
    const {
        categoryKey,
        services,
        selectedIds,
        theme,
        onToggle,
        onCreateService,
        onCreated,
    } = props
    const accent = CATEGORY_ACCENTS[categoryKey]
    const selectedCount = services.filter((s) => selectedIds.has(s.id)).length

    return (
        <Container
            style={{
                flexDirection: 'column',
                marginBottom: theme.spacing(2.5),
            }}
        >
            <Container
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(1.25),
                    paddingBottom: theme.spacing(0.75),
                    borderBottom: `2px solid ${accent}`,
                }}
            >
                {CATEGORY_ICONS[categoryKey] && (
                    <img
                        src={CATEGORY_ICONS[categoryKey] ?? undefined}
                        alt=""
                        style={{
                            height: 28,
                            width: 28,
                            objectFit: 'contain',
                            marginRight: theme.spacing(1),
                        }}
                    />
                )}
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 800,
                        color: accent,
                        letterSpacing: 0.2,
                    }}
                >
                    {getCategoryLabel(categoryKey)}
                </span>
                <div style={{ flex: 1 }} />
                <span
                    style={{
                        ...typographyStyle(theme.typography.caption),
                        fontWeight: 600,
                        color: theme.palette.grey[600],
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 12,
                        padding: theme.spacing(0.25, 1),
                    }}
                >
                    {selectedCount}/{services.length} selected
                </span>
            </Container>

            <Container
                style={{
                    flexWrap: 'wrap',
                    margin: theme.spacing(-0.5),
                }}
            >
                {services.map((service) => (
                    <ServiceTile
                        key={service.id}
                        service={service}
                        selected={selectedIds.has(service.id)}
                        accent={accent}
                        theme={theme}
                        onClick={() => onToggle(service)}
                    />
                ))}
            </Container>
            {onCreateService && categoryKey !== 'OTHER' && (
                <Container style={{ marginTop: theme.spacing(1) }}>
                    <NewServiceTile
                        category={categoryKey}
                        accent={accent}
                        theme={theme}
                        onCreateService={onCreateService}
                        onCreated={onCreated}
                    />
                </Container>
            )}
        </Container>
    )
}

interface NewServiceTileProps {
    category: ServiceCategoryCode
    accent: string
    theme: Theme
    onCreateService: (req: CreateServiceRequest) => CreateServiceActionThunk
    onCreated?: (service: Service) => void
}

const NewServiceTile = (props: NewServiceTileProps) => {
    const { category, accent, theme, onCreateService, onCreated } = props
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [color, setColor] = useState(accent)
    const [submitting, setSubmitting] = useState(false)

    const presets = useMemo(() => {
        const unique = [accent, ...COLOR_PRESETS.filter((c) => c !== accent)]
        return unique.slice(0, 6)
    }, [accent])

    const reset = () => {
        setOpen(false)
        setName('')
        setColor(accent)
        setSubmitting(false)
    }

    const handleCreate = () => {
        const trimmedName = name.trim()
        if (!trimmedName || submitting) return

        setSubmitting(true)
        onCreateService({
            name: trimmedName,
            service_category: category,
            color,
        })
            .then((res) => {
                toast.success(`"${trimmedName}" created`)
                if (res.data) {
                    onCreated?.(res.data)
                }
                reset()
            })
            .catch((e: any) => {
                const key = e?.response?.data?.key
                if (key === 'DUPLICATE_NAME') {
                    toast.error(
                        'A custom service with this name already exists',
                    )
                } else if (key === 'NAME_TOO_LONG') {
                    toast.error('Name must be 30 characters or fewer')
                } else {
                    toast.error(
                        e?.response?.data?.message ??
                            'Failed to create service',
                    )
                }
                setSubmitting(false)
            })
    }

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => {
                    setColor(accent)
                    setOpen(true)
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: TILE_W,
                    minHeight: TILE_H,
                    margin: theme.spacing(0.5),
                    padding: theme.spacing(1, 1.25),
                    border: `1px dashed ${accent}`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: accent,
                }}
            >
                <AddIcon style={{ fontSize: 18, marginRight: 6 }} />
                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        fontWeight: 600,
                    }}
                >
                    New {getCategoryLabel(category)} service
                </span>
            </button>
        )
    }

    const previewName = name.trim() || 'Service name'

    return (
        <Container
            style={{
                flexDirection: 'column',
                width: CREATE_TILE_W,
                margin: theme.spacing(0.5),
                padding: theme.spacing(1.25),
                border: `2px solid ${accent}`,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: '#fff',
                boxShadow: `0 0 0 1px ${accent}22`,
            }}
        >
            <Container
                style={{
                    alignItems: 'center',
                    minHeight: TILE_H - 8,
                    marginBottom: theme.spacing(1.25),
                    padding: theme.spacing(1, 1.25),
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: `${color}14`,
                }}
            >
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: color,
                        marginRight: theme.spacing(1),
                        flexShrink: 0,
                    }}
                />
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'hidden',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: 700,
                            color: name.trim()
                                ? theme.palette.grey[800]
                                : theme.palette.grey[400],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {previewName}
                    </span>
                    <Container style={{ alignItems: 'center' }}>
                        <StarIcon
                            style={{
                                fontSize: 12,
                                color: '#f5a623',
                                marginRight: 3,
                            }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                fontSize: 10,
                                color: theme.palette.grey[500],
                            }}
                        >
                            Custom · {getCategoryLabel(category)}
                        </span>
                    </Container>
                </Container>
            </Container>

            <TextField
                autoFocus
                size="small"
                variant="outlined"
                placeholder="Service name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 30 }}
                helperText={`${name.length}/30`}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate()
                    if (e.key === 'Escape') reset()
                }}
                fullWidth
            />

            <span
                style={{
                    ...typographyStyle(theme.typography.caption),
                    fontWeight: 600,
                    color: theme.palette.grey[600],
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(0.5),
                }}
            >
                Color
            </span>
            <Container style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                {presets.map((preset) => (
                    <button
                        key={preset}
                        type="button"
                        onClick={() => setColor(preset)}
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: preset,
                            border:
                                color.toLowerCase() === preset.toLowerCase()
                                    ? `2px solid ${theme.palette.grey[800]}`
                                    : `2px solid ${theme.palette.grey[200]}`,
                            marginRight: 6,
                            marginBottom: 4,
                            cursor: 'pointer',
                            padding: 0,
                        }}
                        aria-label={preset}
                    />
                ))}
                <Tooltip title="Custom color">
                    <label
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: `2px dashed ${theme.palette.grey[400]}`,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            position: 'relative',
                            background: `conic-gradient(${COLOR_PRESETS.join(
                                ', ',
                            )})`,
                            marginBottom: 4,
                        }}
                    >
                        <input
                            type="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            style={{
                                opacity: 0,
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                            }}
                        />
                    </label>
                </Tooltip>
            </Container>

            <Container
                style={{
                    justifyContent: 'flex-end',
                    marginTop: theme.spacing(1.25),
                }}
            >
                <Button
                    size="small"
                    onClick={reset}
                    disabled={submitting}
                    style={{ textTransform: 'none', marginRight: 8 }}
                >
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    disabled={!name.trim() || submitting}
                    style={{ textTransform: 'none', minWidth: 72 }}
                >
                    {submitting ? (
                        <CircularProgress size={16} color="inherit" />
                    ) : (
                        'Create'
                    )}
                </Button>
            </Container>
        </Container>
    )
}

interface ServiceTileProps {
    service: Service
    selected: boolean
    accent: string
    theme: Theme
    onClick: () => void
}

const ServiceTile = (props: ServiceTileProps) => {
    const { service, selected, accent, theme, onClick } = props
    const isCustom = service.is_custom === true

    const tooltipTitle = isCustom
        ? selected
            ? 'Custom service (this property only) — click to remove'
            : 'Custom service (this property only) — click to add'
        : selected
        ? 'Remove service'
        : 'Add service'

    return (
        <Tooltip
            title={tooltipTitle}
            enterDelay={400}
            leaveDelay={0}
            PopperProps={{
                style: { pointerEvents: 'none' },
            }}
        >
            <button
                type="button"
                onClick={onClick}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: TILE_W,
                    minHeight: TILE_H,
                    margin: theme.spacing(0.5),
                    padding: theme.spacing(1, 1.25),
                    border: selected
                        ? `2px solid ${accent}`
                        : `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: selected ? `${accent}12` : '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    boxShadow: selected ? `0 0 0 1px ${accent}22` : 'none',
                    transition:
                        'border-color 120ms ease, background-color 120ms ease',
                    position: 'relative',
                }}
            >
                <img
                    src={
                        getServiceImageDetails(
                            service.id,
                            service.service_category,
                        ).icon
                    }
                    alt=""
                    style={{
                        maxHeight: 28,
                        maxWidth: 28,
                        marginRight: theme.spacing(1),
                        flexShrink: 0,
                    }}
                />
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'hidden',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: selected ? 700 : 500,
                            color: theme.palette.grey[800],
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {service.name}
                    </span>
                    {isCustom && (
                        <Container
                            style={{
                                alignItems: 'center',
                                marginTop: 1,
                            }}
                        >
                            <StarIcon
                                style={{
                                    fontSize: 12,
                                    color: '#f5a623',
                                    marginRight: 3,
                                }}
                            />
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    fontSize: 10,
                                    color: theme.palette.grey[500],
                                    lineHeight: 1,
                                }}
                            >
                                Custom
                            </span>
                        </Container>
                    )}
                </Container>
                {selected ? (
                    <CheckCircleIcon
                        style={{ color: accent, fontSize: 22, flexShrink: 0 }}
                    />
                ) : (
                    <AddCircleOutlineIcon
                        style={{
                            color: theme.palette.grey[400],
                            fontSize: 22,
                            flexShrink: 0,
                        }}
                    />
                )}
            </button>
        </Tooltip>
    )
}

const TILE_W = 210
const TILE_H = 56
const CREATE_TILE_W = 280
